import type { NodeEntry } from 'slate'
import { Text } from 'slate'

type NormalizeNode<TEntry extends NodeEntry, TOptions> = (entry: TEntry, options?: TOptions) => void

/**
 * Fast path: a plain text node without newlines needs only Slate's base normalization
 * (adjacent-text merging), so the plugin wrapper chain is skipped for it (~56% of normalizeNode
 * calls). That is only sound while no plugin normalizes such text nodes. A plugin that does must
 * declare `normalizesText: true`, which turns the fast path off for the editor (GH #1915);
 * without the flag its text normalizer would silently never run.
 */
export const withTextFastPath = <TEntry extends NodeEntry, TOptions>({
  baseNormalizeNode,
  pluginNormalizeNode,
  pluginsNormalizeText,
}: {
  baseNormalizeNode: NormalizeNode<TEntry, TOptions>
  pluginNormalizeNode: NormalizeNode<TEntry, TOptions>
  pluginsNormalizeText: boolean
}): NormalizeNode<TEntry, TOptions> => {
  if (pluginsNormalizeText) {
    return pluginNormalizeNode
  }
  return (entry, options) => {
    const [node] = entry
    if (Text.isText(node) && !node.text.includes('\n')) {
      baseNormalizeNode(entry, options)
      return
    }
    pluginNormalizeNode(entry, options)
  }
}
